import { friendlyCampaignError } from '../utils/campaignErrors';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  campaignErrorMessage,
  applyRecommendations,
  dismissRecommendation,
  getActivity,
  getAnalysisQuota,
  getConnection,
  getReferrals,
  listAdAccounts,
  listRecommendations,
  runDailyNow,
} from '../api/adpilotApi';
import { demoAdAccounts, demoConnection, demoRecommendations } from '../demoData';
import { channelFor, applyToolFor, actionLabelFor } from '../channels';
import GoogleBudgetEditor from './GoogleBudgetEditor';
import MetaCampaignEditor from './MetaCampaignEditor';
import ChannelTabs, { readActiveChannel } from './ChannelTabs';

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const PRIORITY_RANK = { high: 0, medium: 1, low: 2 };
const FB_OFFSET_1_CURRENCIES = new Set(['CLP', 'ISK', 'JPY', 'KRW', 'PYG', 'TWD', 'VND']);
const FB_MONEY_FIELDS = new Set(['daily_budget', 'bid_amount', 'spend_limit', 'spend_cap']);

const centsToDollars = (cents) => (
  cents === null || cents === undefined ? '—' : dollars.format(Number(cents) / 100)
);

function formatFacebookValue(key, value, currency = 'USD') {
  if (value == null || value === '' || !FB_MONEY_FIELDS.has(key) || !Number.isFinite(Number(value))) return String(value);
  const code = String(currency).toUpperCase();
  const amount = FB_OFFSET_1_CURRENCIES.has(code) ? Number(value) : Number(value) / 100;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(amount);
}

function formatSuggestedValue(key, value, currency) {
  if (value == null || value === '') return '';
  if (/_micros$/.test(key) && Number.isFinite(Number(value))) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: String(currency || 'USD').toUpperCase(),
    }).format(Number(value) / 1_000_000);
  }
  if (currency && FB_MONEY_FIELDS.has(key)) return formatFacebookValue(key, value, currency);
  if (Array.isArray(value)) return value.map((item) => formatSuggestedValue(key, item, currency)).filter(Boolean).join(', ');
  if (typeof value !== 'object') {
    const text = String(value);
    return /zip/.test(key) ? text.replace(/^[A-Z]{2}:/, '') : text;
  }
  if (value.name != null) return String(value.name);
  if (value.key != null && Object.keys(value).length === 1) return formatSuggestedValue(key, value.key, currency);
  return Object.entries(value)
    .filter(([, nestedValue]) => nestedValue != null && nestedValue !== '')
    .map(([nestedKey, nestedValue]) => `${nestedKey.replace(/_/g, ' ')}: ${formatSuggestedValue(nestedKey, nestedValue, currency)}`)
    .join('; ');
}

function RecommendationsPage({ user }) {
  // The page's channel dimension. Every load below is scoped to it, and
  // switching tabs re-runs the whole load against the other provider's
  // connection — the two channels never mix state.
  const [activeChannel, setActiveChannel] = useState(readActiveChannel);
  const channel = channelFor(activeChannel);
  const [connection, setConnection] = useState(user.preview ? demoConnection : null);
  const [accountName, setAccountName] = useState(user.preview ? demoAdAccounts[0].name : null);
  const [hasConnection, setHasConnection] = useState(user.preview ? true : null);
  const [recommendations, setRecommendations] = useState(user.preview ? demoRecommendations : []);
  const [budgetRecommendation, setBudgetRecommendation] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [activeOperation, setActiveOperation] = useState(null);
  const [lookbackDays, setLookbackDays] = useState(7);
  const [loading, setLoading] = useState(!user.preview);
  const [network, setNetwork] = useState(null);
  const [analysisQuota, setAnalysisQuota] = useState(user.preview ? { limit: 3, remaining: 3 } : null);
  const [analyzing, setAnalyzing] = useState(() => new URLSearchParams(window.location.search).has('analyzing'));
  const analyzingRef = useRef(analyzing);
  const inFlight = useRef(false);
  // The channel the CURRENT load belongs to. A tab switch mid-flight resets
  // the inFlight gate (so the new channel's load starts immediately instead
  // of waiting for the 15s tick) and this ref makes the superseded response
  // inert — without it the old channel's data would paint the new tab.
  const activeChannelRef = useRef(activeChannel);
  const applyingIds = useRef(new Set());
  const loadedAccount = useRef(null);
  const BudgetEditor = activeChannel === 'google-ads' ? GoogleBudgetEditor : MetaCampaignEditor;
  const userId = user.id;
  useEffect(() => { setBudgetRecommendation(null); }, [connection?.connection_id, connection?.ad_account_id]);

  const load = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const loadChannel = activeChannelRef.current;
    const stale = () => activeChannelRef.current !== loadChannel;
    try {
      let current;
      try {
        current = (await getConnection({ userId, provider: loadChannel })).connection;
      } catch (connError) {
        if (stale()) return;
        if (connError?.response?.status === 404) {
          setConnection(null);
          setHasConnection(false);
          setRecommendations([]);
          return;
        }
        throw connError;
      }
      if (stale()) return;
      setConnection(current);
      setHasConnection(true);
      if (current?.connection_id) {
        const accountKey = `${current.connection_id}:${current.ad_account_id}`;
        if (loadChannel === 'facebook' && current.ad_account_id && (!current.access_role || current.access_role === 'owner') && loadedAccount.current !== accountKey) {
          try {
            const accounts = await listAdAccounts({ userId, connectionId: current.connection_id });
            setAccountName(accounts.find((account) => account.ad_account_id === current.ad_account_id)?.name || null);
            loadedAccount.current = accountKey;
          } catch {
            // The brief remains usable if Meta cannot refresh the display name.
          }
        } else if (loadChannel !== 'facebook') {
          setAccountName(current.ad_account_id || null);
        }
        const [proposed, applying, quota] = await Promise.all([
          listRecommendations({ userId, connectionId: current.connection_id, status: 'proposed' }),
          listRecommendations({ userId, connectionId: current.connection_id, status: 'applying' }),
          getAnalysisQuota({ userId, connectionId: current.connection_id }).catch(() => null),
        ]);
        if (stale()) return;
        if (quota) setAnalysisQuota(quota);
        // A recommendation can cross states between reads. Applying wins so
        // it is never rendered twice or left actionable from stale data.
        const byId = new Map([...proposed, ...applying].map((rec) => [rec.id, rec]));
        setRecommendations([...byId.values()]);
        const nowApplying = new Set(applying.map((rec) => rec.id));
        const settled = [...applyingIds.current].filter((id) => !nowApplying.has(id));
        let activity;
        if (settled.length > 0) {
          activity = await getActivity({ userId, connectionId: current.connection_id });
          const outcomes = activity.applies.filter((item) => settled.includes(item.recommendation_id));
          const failed = outcomes.filter((item) => item.status === 'failed');
          const failureReason = failed.map((item) => item.error).find(Boolean);
          setMessage(failed.length
            ? `${failed.length} change${failed.length === 1 ? '' : 's'} could not be applied${failureReason ? `: ${friendlyCampaignError(failureReason)}` : '. Review Activity and try again.'}`
            : `${settled.length} change${settled.length === 1 ? '' : 's'} applied successfully in ${channel.copy.appliedIn}.`);
          if (failed.length === 0) getReferrals().then(setNetwork).catch(() => {});
        }
        applyingIds.current = nowApplying;
        if (analyzingRef.current) {
          activity ||= await getActivity({ userId, connectionId: current.connection_id });
          const requestId = activity.runs.find((run) => run.request_id)?.request_id;
          const requestedRuns = activity.runs.filter((run) => run.request_id === requestId);
          if (requestedRuns.length > 0 && requestedRuns.every((run) => run.status !== 'pending')) {
            setAnalyzing(false);
            getReferrals().then(setNetwork).catch(() => {});
            const failed = requestedRuns.filter((run) => run.status === 'failed').length;
            if (failed > 0) {
              setMessage(`Analysis finished with ${failed} campaign issue${failed === 1 ? '' : 's'}. Review Activity for details.`);
            } else if (requestedRuns.every((run) => run.status === 'skipped')) {
              setMessage('No delivery was found in this period. Try a longer analysis window or choose an active campaign.');
            } else if (byId.size === 0) {
              setMessage('Analysis complete. Your current budgets do not need a change.');
            }
          }
        }
      }
    } catch (loadError) {
      setError(campaignErrorMessage(loadError, 'Failed to load your brief'));
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (user.preview) return undefined;
    load();
    if (hasConnection === false) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, hasConnection, user.preview, activeChannel]);

  // A tab switch is a fresh page for the other channel — no state bleeds.
  const switchChannel = (key) => {
    if (key === activeChannel) return;
    activeChannelRef.current = key;
    setBudgetRecommendation(null);
    // Free the gate: a load in flight for the old channel is now inert (the
    // stale() guards drop its results), and the effect's re-run must not be
    // swallowed waiting for it.
    inFlight.current = false;
    setActiveChannel(key);
    setConnection(null);
    setHasConnection(user.preview ? true : null);
    setRecommendations(user.preview ? demoRecommendations : []);
    setSelectedIds(new Set());
    setAccountName(null);
    setMessage(null);
    setError(null);
    setLoading(!user.preview);
    loadedAccount.current = null;
  };

  useEffect(() => {
    if (user.preview) return;
    getReferrals().then(setNetwork).catch(() => {});
  }, [user.preview]);

  useEffect(() => {
    analyzingRef.current = analyzing;
  }, [analyzing]);

  useEffect(() => {
    if (!analyzing) return undefined;
    const timer = setTimeout(() => setAnalyzing(false), 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [analyzing]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = async (ids = [...selectedIds]) => {
    if (budgetRecommendation) return;
    if (ids.length === 0) {
      setError('Choose at least one change to approve.');
      return;
    }
    if (!connection?.connection_id) {
      setError(channel.copy.connectFirst);
      return;
    }
    if (user.preview) {
      setRecommendations((prev) => prev.map((rec) => (
        ids.includes(rec.id) ? { ...rec, status: 'applying' } : rec
      )));
      setSelectedIds(new Set());
      setMessage(`${ids.length} change${ids.length === 1 ? '' : 's'} approved. AdPilot is applying ${ids.length === 1 ? 'it' : 'them'} now.`);
      return;
    }
    setBusy(true);
    setActiveOperation('apply');
    setError(null);
    setMessage(null);
    try {
      const result = await applyRecommendations({
        userId, connectionId: connection.connection_id, recommendationIds: ids,
      });
      setMessage(`${result.dispatched?.length || 0} change${ids.length === 1 ? '' : 's'} approved. AdPilot is applying ${ids.length === 1 ? 'it' : 'them'} now.`);
      setSelectedIds(new Set());
      await load();
    } catch (applyError) {
      setError(campaignErrorMessage(applyError, 'Approval failed'));
    } finally {
      setBusy(false);
      setActiveOperation(null);
    }
  };

  const handleDismiss = async (id) => {
    if (!connection?.connection_id) return;
    if (user.preview) {
      setRecommendations((prev) => prev.filter((rec) => rec.id !== id));
      return;
    }
    try {
      await dismissRecommendation({
        userId, connectionId: connection.connection_id, recommendationId: id,
      });
      setRecommendations((prev) => prev.filter((rec) => rec.id !== id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (dismissError) {
      setError(campaignErrorMessage(dismissError, 'Could not dismiss this change'));
    }
  };

  const handleRunNow = async () => {
    if (!connection?.connection_id) return;
    if (user.preview) {
      setMessage('Preview refreshed. Your campaign brief is up to date.');
      return;
    }
    setBusy(true);
    setActiveOperation('analysis');
    setError(null);
    setMessage(null);
    try {
      const stats = await runDailyNow({
        userId, connectionId: connection.connection_id, lookbackDays,
      });
      setAnalyzing(Boolean(stats.dispatched));
      if (stats.remaining != null) {
        setAnalysisQuota({ limit: stats.limit || 3, remaining: stats.remaining, retry_at: stats.retry_at || null });
      }
      if (stats.rate_limited) {
        const retry = stats.retry_at ? ` Your next analysis is available ${new Date(stats.retry_at).toLocaleString()}.` : '';
        setMessage(`You’ve used all ${stats.limit || 3} analyses in the last 24 hours.${retry}`);
      } else if (stats.busy) {
        setMessage('An analysis is already running. Your brief will update automatically.');
      } else if (stats.dispatched) {
        const partial = stats.failed ? ` ${stats.failed} campaign${stats.failed === 1 ? '' : 's'} could not be dispatched.` : '';
        setMessage(`Campaign agent dispatched ${stats.dispatched} run${stats.dispatched === 1 ? '' : 's'} using the last ${lookbackDays} days. ${stats.remaining} of ${stats.limit} analyses remain in your rolling 24-hour window.${partial}`);
      } else {
        setMessage('No campaigns were queued for analysis.');
      }
    } catch (runError) {
      setError(campaignErrorMessage(runError, 'Could not start analysis'));
    } finally {
      setBusy(false);
      setActiveOperation(null);
    }
  };

  const proposed = recommendations.filter((rec) => rec.status !== 'applying');
  const applying = recommendations.length - proposed.length;
  const highImpact = proposed.filter((rec) => rec.priority === 'high').length;
  const recommendationGroups = Array.from(recommendations.reduce((groups, rec) => {
    const campaignName = rec.campaign_name || 'Campaign';
    if (!groups.has(campaignName)) groups.set(campaignName, []);
    groups.get(campaignName).push(rec);
    return groups;
  }, new Map()))
    // Within a campaign: high → medium → low impact, top to bottom; within
    // equal impact, actionable recs before advisory ones (an approver works
    // the applicable list first). Stable beyond that, so recs keep the run's
    // own order.
    .map(([campaignName, rows]) => [
      campaignName,
      [...rows].sort((a, b) => (
        ((PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1))
        || ((a.suggested_values?.auto_applicable === false ? 1 : 0)
          - (b.suggested_values?.auto_applicable === false ? 1 : 0))
      )),
    ]);
  const runningAnalysis = activeOperation === 'analysis' || analyzing;
  const runningApply = activeOperation === 'apply' || applying > 0;
  const agentStatus = loading ? 'Connecting tools' : runningAnalysis ? 'Running analysis' : runningApply ? 'Applying changes' : proposed.length ? 'Waiting for approval' : 'Ready';
  const workflow = loading
    ? [[channel.tools.read, `Connecting to ${channel.copy.appliedIn}`, 'running'], [channel.tools.plan, 'Waiting for campaign data', 'queued'], [channel.tools.apply, 'Always requires approval', 'locked']]
    : runningAnalysis
      ? [[channel.tools.read, `Reading ${lookbackDays} days of delivery`, 'running'], [channel.tools.plan, 'Evaluating pacing opportunities', 'queued'], [channel.tools.apply, 'Blocked until you approve', 'locked']]
    : recommendations.length
      ? [[channel.tools.read, 'Latest delivery window read', 'complete'], [channel.tools.plan, `${proposed.length} safe change${proposed.length === 1 ? '' : 's'} prepared`, 'complete'], [channel.tools.apply, runningApply ? 'Applying approved changes' : 'Waiting for your approval', runningApply ? 'running' : 'approval']]
      : [[channel.tools.read, channel.copy.readReady, 'ready'], [channel.tools.plan, 'Ready to evaluate pacing', 'ready'], [channel.tools.apply, 'Always requires approval', 'locked']];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Campaign agent</p>
          <h1 className="page-title">Your marketing operator.</h1>
          <p className="page-subtitle">{channel.copy.briefSubtitle}</p>
        </div>
        {hasConnection && <span className="agent-online"><i /> Watching {accountName || connection?.ad_account_id || channel.label}</span>}
      </header>

      <ChannelTabs active={activeChannel} onSelect={switchChannel} />

      {error && <div className="notice notice-error" role="alert">{error}</div>}
      {message && <div className="notice" role="status">{message}</div>}

      {hasConnection !== false && (
        <section className="agent-console" aria-label="Campaign agent">
          <div className="agent-console-head">
            <div className="agent-identity">
              <span className="agent-avatar">A</span>
              <div><strong>{channel.agentTitle}</strong><small>{channel.agentSubtitle}</small></div>
            </div>
            <div className="agent-head-actions">
              <label className="analysis-window">
                <span>Window</span>
                <select
                  aria-label="Analysis window"
                  value={lookbackDays}
                  disabled={busy || analyzing || loading}
                  onChange={(event) => setLookbackDays(Number(event.target.value))}
                >
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                </select>
              </label>
              <span className={`agent-state ${runningAnalysis || runningApply || loading ? 'agent-state-running' : ''}`}>
                {(runningAnalysis || runningApply || loading) && <span className="spinner" aria-hidden="true" />}
                {agentStatus}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="agent-command"
            aria-label="Run campaign agent"
            disabled={busy || analyzing || loading || !hasConnection || analysisQuota?.remaining === 0}
            onClick={handleRunNow}
          >
            <span className="command-prompt">›</span>
            <span><strong>Review performance and find safe improvements</strong><small>Analyze the last {lookbackDays} days · Never publish without approval</small></span>
            <span className="run-agent">{runningAnalysis ? 'Running…' : analysisQuota ? `${analysisQuota.remaining} left · Run agent →` : 'Checking allowance…'}</span>
          </button>
          <div className="agent-tools" aria-label="Agent workflow">
            {workflow.map(([tool, description, status]) => (
              <div key={tool} className={`agent-tool agent-tool-${status}`}>
                <span className="tool-status" />
                <code>{tool}</code>
                <span>{description}</span>
                <em>{status}</em>
              </div>
            ))}
          </div>
          <footer>{analysisQuota ? `${analysisQuota.remaining} of ${analysisQuota.limit} analyses available in the rolling 24-hour window` : 'Checking analysis allowance'} <span>·</span> Changes require explicit approval</footer>
        </section>
      )}

      {!loading && hasConnection === false && (
        <div className="agent-turn">
          <span className="agent-avatar">A</span>
          <div>
            <strong>Let’s connect your first campaign.</strong>
            <p>{channel.copy.emptyPitch}</p>
            <Link to="/connect" className="btn btn-primary">{channel.copy.connectCta}</Link>
          </div>
        </div>
      )}

      {!loading && hasConnection && recommendations.length === 0 && (
        <div className="agent-turn">
          <span className="agent-avatar">A</span>
          <div>
            <strong className={busy || analyzing ? 'loading-label' : undefined}>
              {(busy || analyzing) && <span className="spinner" aria-hidden="true" />}
              {busy || analyzing ? 'I’m analyzing your campaigns.' : 'You’re all caught up.'}
            </strong>
            <p>{busy || analyzing
              ? `I’m reviewing the last ${lookbackDays} days of performance and delivery. This brief will update automatically when I’m done.`
              : 'No recommendations need your attention right now. I’ll keep watching, or I can review the latest data again.'}</p>
          </div>
        </div>
      )}

      {!loading && network?.eligible && !runningAnalysis && (
        <section className="network-nudge">
          <div><span className="eyebrow">Result unlocked</span><strong>Invite another business to AdPilot.</strong><p>You have {network.slots_remaining} referral link{network.slots_remaining === 1 ? '' : 's'} available.</p></div>
          <Link to="/referrals" className="btn btn-secondary">Share AdPilot →</Link>
        </section>
      )}

      {budgetRecommendation && connection && <BudgetEditor key={budgetRecommendation.id} connectionId={connection.connection_id}
        campaignId={budgetRecommendation.platform_campaign_id} recommendationId={budgetRecommendation.id}
        onClose={() => { setBudgetRecommendation(null); load(); }} onChanged={load} />}

      {recommendations.length > 0 && (
        <>
          <div className="agent-turn">
            <span className="agent-avatar">A</span>
            <div>
              <strong>I reviewed your campaign performance.</strong>
              <p>
                I found {proposed.length} change{proposed.length === 1 ? '' : 's'} worth reviewing
                {highImpact ? `, including ${highImpact} high-impact move${highImpact === 1 ? '' : 's'}` : ''}.
                {applying ? ` I’m already applying ${applying} approved change${applying === 1 ? '' : 's'}.` : ' Nothing changes until you approve it.'}
              </p>
            </div>
          </div>
          <div className="brief-toolbar">
            <p>{proposed.length} recommendation{proposed.length === 1 ? '' : 's'} from the latest analysis</p>
            <div className="brief-toolbar-actions">
              <button type="button" className="btn btn-secondary" disabled={busy || Boolean(budgetRecommendation) || selectedIds.size === 0} onClick={() => handleApply()}>
                Approve selected ({selectedIds.size})
              </button>
              <button type="button" className="btn btn-primary" disabled={busy || Boolean(budgetRecommendation) || proposed.filter((rec) => rec.suggested_values?.auto_applicable !== false).length === 0} onClick={() => handleApply(proposed.filter((rec) => rec.suggested_values?.auto_applicable !== false).map((rec) => rec.id))}>
                Approve &amp; apply all
              </button>
            </div>
          </div>

          {recommendationGroups.map(([campaignName, campaignRecommendations]) => (
            <section key={campaignName} className="recommendation-group">
              <div className="section-heading recommendation-group-heading">
                <div>
                  <h2>{campaignName}</h2>
                  <span>
                    {campaignRecommendations.length} recommendation{campaignRecommendations.length === 1 ? '' : 's'} to review
                    {(() => {
                      const advisoryCount = campaignRecommendations.filter((rec) => rec.suggested_values?.auto_applicable === false).length;
                      return advisoryCount ? ` · ${advisoryCount} advisory` : '';
                    })()}
                  </span>
                </div>
              </div>
              {campaignRecommendations.map((rec) => {
            const suggested = rec.suggested_values || {};
            const isApplying = rec.status === 'applying';
            const change = Number(suggested.change_pct || 0);
            // Meta recs carry budget cents; Google recs carry the optimizer's
            // whole suggestion (any of 15 action types). Render whichever
            // value the rec actually has instead of assuming budgets.
            const isBudgetShaped = suggested.new_budget_cents != null;
            // The proposed change renders as FULL-WIDTH structured content
            // inside Review details, in whichever shape the suggestion
            // actually has — a list for keyword arrays, labeled rows for a
            // values dict, a wrapped paragraph for prose. The right-hand
            // column exists only for budget recs, where one dollar figure is
            // genuinely glanceable; an ad_copy suggestion quoting nine
            // headlines is review material, not a column.
            const proposedList = Array.isArray(suggested.recommended_value)
              ? suggested.recommended_value.map((item) => (
                item && typeof item === 'object'
                  ? Object.values(item).filter((part) => part != null && part !== '').map((part) => formatSuggestedValue('', part, suggested.account_currency)).join(' · ')
                  : String(item)
              ))
              : null;
            const proposedObject = suggested.suggested_values && typeof suggested.suggested_values === 'object'
              ? suggested.suggested_values
              : suggested.recommended_value && typeof suggested.recommended_value === 'object'
                ? suggested.recommended_value
                : null;
            const proposedRows = !proposedList && proposedObject
              ? Object.entries(proposedObject)
                .filter(([, value]) => value != null && value !== '')
                .map(([key, value]) => `${key.replace(/_/g, ' ')}: ${formatSuggestedValue(
                  key, value, suggested.account_currency,
                )}`)
              : null;
            const proposedText = !proposedList && !proposedRows
              && typeof suggested.recommended_value === 'string'
              ? suggested.recommended_value
              : null;
            const advisory = suggested.auto_applicable === false;
            // One compact from → to line, visible without expanding, for the
            // shapes that HAVE a compact rendering. Prose changes (ad copy,
            // long rewrites) get none — their home is Review details.
            const SHORT = 48;
            const shortish = (value) => typeof value === 'string' && value.length > 0 && value.length <= SHORT;
            const currentDisplay = formatSuggestedValue('current_value', suggested.current_value, suggested.account_currency);
            const recommendedDisplay = formatSuggestedValue('recommended_value', suggested.recommended_value, suggested.account_currency);
            let changeStrip = null;
            if (isBudgetShaped) {
              changeStrip = `${centsToDollars(suggested.current_budget_cents)} → ${centsToDollars(suggested.new_budget_cents)}${change ? ` · ${change > 0 ? '+' : ''}${change}%` : ''}`;
            } else if (shortish(suggested.current_value) && shortish(suggested.recommended_value)) {
              changeStrip = `${suggested.current_value} → ${suggested.recommended_value}`;
            } else if (currentDisplay && recommendedDisplay && currentDisplay.length <= 96 && recommendedDisplay.length <= 96) {
              changeStrip = `${currentDisplay} → ${recommendedDisplay}`;
            } else if (proposedRows && proposedRows.length <= 2 && proposedRows.every((row) => row.length <= SHORT)) {
              changeStrip = proposedRows.join(' · ');
            } else if (proposedList) {
              const plural = actionLabelFor(rec.action_type).toLowerCase();
              const noun = proposedList.length === 1 && plural.endsWith('s') ? plural.slice(0, -1) : plural;
              changeStrip = `${proposedList.length} ${noun}`;
            } else if (shortish(suggested.recommended_value)) {
              changeStrip = suggested.recommended_value;
            }
            return (
              <article key={rec.id} className={`card recommendation-card ${isApplying ? 'applying-card' : ''}`}>
                <div className="recommendation-main">
                  <input
                    type="checkbox"
                    className="select-box"
                    aria-label={`Select ${rec.title}`}
                    checked={selectedIds.has(rec.id)}
                    disabled={isApplying || advisory || Boolean(budgetRecommendation)}
                    onChange={() => toggle(rec.id)}
                  />
                  <div className="recommendation-copy">
                    <div className="agent-recommendation-kicker">
                      <span className="agent-recommendation-mark" aria-hidden="true">✦</span>
                      <span>AdPilot recommendation</span>
                      <span className="agent-recommendation-source">{channel.label}</span>
                    </div>
                    <div className="recommendation-meta">
                      {/* The reader's first anchor is WHAT KIND of change this
                          is — recs are not budget-only on either channel.
                          Legacy pacing rows can predate action_type; their
                          budget shape names them. */}
                      <span className="action-chip">{actionLabelFor(rec.action_type || (isBudgetShaped ? 'daily_budget' : null))}</span>
                      <span className={`priority-pill ${rec.priority === 'high' ? '' : 'priority-medium'}`}>
                        {isApplying ? 'Applying' : `${rec.priority || 'medium'} impact`}
                      </span>
                      {advisory && <span className="priority-pill priority-advisory">advisory</span>}
                    </div>
                    <h2>{rec.title}</h2>
                    {changeStrip && <p className="change-strip">{changeStrip}</p>}
                    <details className="recommendation-details">
                      <summary>Review details</summary>
                      <p>{rec.rationale || rec.description || 'AdPilot found a pacing opportunity in this campaign.'}</p>
                      {(proposedList || proposedRows || proposedText) && (
                        <div className="recommendation-proposed-value">
                          <strong>Proposed change</strong>
                          {proposedText && <p>{proposedText}</p>}
                          {(proposedList || proposedRows) && (
                            <ul>
                              {(proposedList || proposedRows).map((line) => <li key={line}>{line}</li>)}
                            </ul>
                          )}
                        </div>
                      )}
                      {suggested.current_value != null && currentDisplay && (
                        <p className="recommendation-current-value"><strong>Current setup</strong>{currentDisplay}</p>
                      )}
                      <p className="recommendation-tool-line">Applied via <code className="tool-call">{applyToolFor(channel, rec.action_type)}</code></p>
                    </details>
                  </div>
                </div>
                {!isApplying && (
                  <div className="recommendation-actions">
                    <button type="button" className="text-button text-button-danger" onClick={() => handleDismiss(rec.id)}>Dismiss</button>
                    {!user.preview && ['facebook', 'google-ads'].includes(activeChannel) && (!connection?.access_role || connection.access_role === 'owner') && !advisory
                      && (activeChannel === 'google-ads' ? rec.action_type === 'budget' : isBudgetShaped || ['daily_budget', 'campaign_daily_budget'].includes(rec.action_type)) && <button type="button" className="text-button"
                        disabled={busy || Boolean(budgetRecommendation)} onClick={() => { setSelectedIds(new Set()); setBudgetRecommendation(rec); }}>Adjust budget</button>}
                    {advisory ? (
                      <span className="text-button" aria-disabled="true" title={`Do this in ${channel.copy.appliedIn} directly — AdPilot cannot apply it safely.`}>
                        Advisory — do in {channel.copy.appliedIn}
                      </span>
                    ) : (
                      <button type="button" className="text-button" disabled={busy || Boolean(budgetRecommendation)} onClick={() => handleApply([rec.id])}>Approve &amp; apply →</button>
                    )}
                  </div>
                )}
                {isApplying && (
                  <div className="recommendation-actions recommendation-applying-status" role="status">
                    <button type="button" className="text-button" disabled>
                      <span className="spinner" aria-hidden="true" /> Applying in {channel.copy.appliedIn}…
                    </button>
                  </div>
                )}
              </article>
            );
              })}
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export default RecommendationsPage;
